// @flow
import React, { useState } from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DownloadIcon from '@mui/icons-material/Download';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { useTheme } from '@mui/material/styles';
import { createClasses } from '../../../utils/stylesUtils';
import { downloadErrorReport, formatNumber } from '../csvTemplateUtils';
import resultsViewStyles from './ResultsView.Style';

type ImportStatus = {
  status: 'processing' | 'completed' | 'failed',
  totalRows: number,
  processedRows: number,
  created: number,
  skipped: number,
  failed: number,
  errors: Array<{row: number, phone: string, reason: string}>,
  completedAt?: string
};

type Props = {
  importStatus: ImportStatus,
  onDone: () => void
};

const ResultsView = ({ importStatus, onDone }: Props) => {
  const theme = useTheme();
  const classes = createClasses(resultsViewStyles, theme);

  const [errorsExpanded, setErrorsExpanded] = useState<boolean>(false);

  const { created, skipped, failed, errors, status } = importStatus;
  const hasErrors = errors && errors.length > 0;
  const isSuccess = status === 'completed' && created > 0;
  const isFailed = status === 'failed';

  const handleDownloadErrors = () => {
    if (errors && errors.length > 0) {
      downloadErrorReport(errors);
    }
  };

  const getSummaryText = (): string => {
    if (isFailed) {
      return 'Import failed. Please try again.';
    }

    const parts = [];
    if (created > 0) {
      parts.push(`${formatNumber(created)} customer${created !== 1 ? 's' : ''} created`);
    }
    if (skipped > 0) {
      parts.push(`${formatNumber(skipped)} duplicate${skipped !== 1 ? 's' : ''} skipped`);
    }
    if (failed > 0) {
      parts.push(`${formatNumber(failed)} failed`);
    }

    if (parts.length === 0) {
      return 'No customers were imported.';
    }

    return parts.join(', ') + '.';
  };

  return (
    <Box sx={classes.container}>
      <Box sx={classes.content}>
        <Box sx={classes.header}>
          {isSuccess ? (
            <CheckCircleIcon sx={classes.successIcon} />
          ) : (
            <ErrorIcon sx={classes.errorIcon} />
          )}
          <Typography variant="h5" sx={classes.title}>
            {isSuccess ? 'Import Complete!' : 'Import Results'}
          </Typography>
        </Box>

        {isFailed && (
          <Alert severity="error" sx={classes.alert}>
            The import process encountered an error. Please try again or contact support if the issue persists.
          </Alert>
        )}

        <Typography variant="body1" sx={classes.summary}>
          {getSummaryText()}
        </Typography>

        <Box sx={classes.counters}>
          <Box sx={{
            ...classes.counter,
            ...(created > 0 ? classes.counterHighlight : {})
          }}>
            <Box sx={classes.counterIcon}>
              <PersonAddIcon sx={classes.createdIcon} />
            </Box>
            <Typography variant="h4" sx={classes.counterValue}>
              {formatNumber(created)}
            </Typography>
            <Typography variant="body2" sx={classes.counterLabel}>
              Created
            </Typography>
          </Box>

          <Box sx={classes.counter}>
            <Box sx={classes.counterIcon}>
              <SkipNextIcon sx={classes.skippedIcon} />
            </Box>
            <Typography variant="h4" sx={classes.counterValue}>
              {formatNumber(skipped)}
            </Typography>
            <Typography variant="body2" sx={classes.counterLabel}>
              Skipped
            </Typography>
          </Box>

          <Box sx={{
            ...classes.counter,
            ...(failed > 0 ? classes.counterError : {})
          }}>
            <Box sx={classes.counterIcon}>
              <ErrorOutlineIcon sx={classes.failedIcon} />
            </Box>
            <Typography variant="h4" sx={classes.counterValue}>
              {formatNumber(failed)}
            </Typography>
            <Typography variant="body2" sx={classes.counterLabel}>
              Failed
            </Typography>
          </Box>
        </Box>

        {hasErrors && (
          <Box sx={classes.errorsSection}>
            <Box
              sx={classes.errorsHeader}
              onClick={() => setErrorsExpanded(!errorsExpanded)}
            >
              <Typography variant="subtitle1" sx={classes.errorsTitle}>
                Error Details ({errors.length} error{errors.length !== 1 ? 's' : ''})
              </Typography>
              <IconButton size="small">
                {errorsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            <Collapse in={errorsExpanded}>
              <TableContainer sx={classes.tableContainer}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={classes.tableHeaderCell}>Row</TableCell>
                      <TableCell sx={classes.tableHeaderCell}>Phone</TableCell>
                      <TableCell sx={classes.tableHeaderCell}>Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {errors.slice(0, 100).map((error, index) => (
                      <TableRow key={index}>
                        <TableCell sx={classes.tableCell}>{error.row}</TableCell>
                        <TableCell sx={classes.tableCell}>{error.phone || '-'}</TableCell>
                        <TableCell sx={classes.tableCell}>{error.reason}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {errors.length > 100 && (
                <Typography variant="caption" sx={classes.moreErrors}>
                  Showing first 100 errors. Download the full report for all {errors.length} errors.
                </Typography>
              )}
            </Collapse>

            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadErrors}
              sx={classes.downloadButton}
            >
              Download Error Report
            </Button>
          </Box>
        )}
      </Box>

      <Box sx={classes.actions}>
        <Button
          variant="contained"
          onClick={onDone}
          size="large"
        >
          Done
        </Button>
      </Box>
    </Box>
  );
};

export default ResultsView;
